/**
 * The wait a `429` names in its `Retry-After` header, carried to the screen (plan-008 ST-RV2).
 *
 * ── WHY A LEDGER ───────────────────────────────────────────────────────────────────────────
 * The generated client's `ApiResult` carries the status, the body, the rate-limit window and the
 * request id — not `Retry-After`. The envelope's `message` is never parsed. So the console's own
 * `fetch` (the one `apiGateway` hands the client) notes the header of every `429` against the
 * response's `X-WeTalk-Request-Id`, and a slice that wants the wait takes it by the request id the
 * `ApiResult` carries. Keyed by request id, two refusals answered at once cannot swap their waits;
 * a refusal without a request id has no wait, and the screen then shows the API's own sentence.
 * A note nobody takes is dropped after {@link RETRY_AFTER_NOTE_MAX} newer ones.
 *
 * Only the delta-seconds form is read (`Retry-After: 42`), the only form the API sends; an
 * HTTP-date or anything else is "no wait known", never a guess.
 */
import { ApiError, type ApiResult } from "@wetalk/api-client";
import { retryAfterSecondOfHeader } from "@wetalk/view-model/retry-after";

/** Moved to `@wetalk/view-model/retry-after` (plan-034 VIEW-PKG); re-exported here so no portal importer changes. */
export { retryAfterSecondOfHeader };

/** How many unclaimed `429` notes the ledger keeps before it forgets the oldest. */
export const RETRY_AFTER_NOTE_MAX = 32;

export interface RetryAfterLedger {
  /** The `fetch` to give the generated client: the answer is untouched, a `429`'s wait is noted. */
  readonly fetch: typeof globalThis.fetch;
  /** The wait noted for this request id, forgotten once taken; `null` when none was. */
  readonly take: (requestId: string | null) => number | null;
}

export function retryAfterLedger(fetch: typeof globalThis.fetch): RetryAfterLedger {
  const noted = new Map<string, number>();
  return {
    fetch: async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const response = await fetch(input, init);
      if (response.status === 429) {
        const requestId = response.headers.get("x-wetalk-request-id");
        const second = retryAfterSecondOfHeader(response.headers.get("retry-after"));
        if (requestId !== null && second !== null) {
          noted.set(requestId, second);
          while (noted.size > RETRY_AFTER_NOTE_MAX) {
            const oldest = noted.keys().next();
            if (oldest.done === true) break;
            noted.delete(oldest.value);
          }
        }
      }
      return response;
    },
    take: (requestId) => {
      if (requestId === null) return null;
      const second = noted.get(requestId);
      if (second === undefined) return null;
      noted.delete(requestId);
      return second;
    },
  };
}

/** A refused call that named how long to wait: the API's `ApiError`, plus its `Retry-After`. */
export class RetryAfterApiError extends ApiError {
  /** Whole seconds from `Retry-After`; `null` when the answer named none. */
  readonly retryAfterSecond: number | null;
  constructor(result: ApiResult, retryAfterSecond: number | null) {
    super(result.status, result.body, result.requestId);
    this.retryAfterSecond = retryAfterSecond;
  }
}

/**
 * Raises a `429` as a {@link RetryAfterApiError} with its noted wait; any other answer passes
 * through untouched, for the slice's own `assertOk`.
 */
export function refuseWithRetryAfter(result: ApiResult, take: (requestId: string | null) => number | null): void {
  if (result.status === 429) throw new RetryAfterApiError(result, take(result.requestId));
}

/** The wait a refusal named, or `null` when it is not a `429` that named one. */
export function retryAfterSecondOf(error: unknown): number | null {
  return error instanceof RetryAfterApiError ? error.retryAfterSecond : null;
}
